import * as React from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { sectionTextMarkers } from '../helpers/customTextMarkers';
import {
  PanelMarker,
  BloodworkAnalysis,
  BloodworkNote,
  FurtherConsiderations,
  Section,
} from '../models';
import { FurtherConsiderationsSection } from './bloodworkz/further-considerations-section';
import { ViscosityInsights } from './bloodworkz/viscosity-insights';
import { AboutSection } from './bloodworkz/about-section';
import { ReferenceRangeAnalysisContainer } from '../containers/bloodwork-ref-range-analysis-container';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { BloodworkNotes } from './bloodworkz/bloodwork-notes';
import { SupplementsEngine } from '../services/supplements-engine';
import { supplementsVisible } from '../utils/supplementsVisibility';
import { sectionHasReferencesTab } from './bloodworkz/reference-tab-sections';

type Props = {
  section: Section;
  panelMarkers: PanelMarker[];
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  bloodworkEntryStore: BloodworkEntryStore;
  // Optional: sections with no bibliography anchor (e.g. the practitioner's own
  // custom markers) omit it, and the "learn more" affordance is skipped.
  infoLink?: string;
  enabledSupplementBrands?: string[];
  /**
   * The name of the practitioner-named section this panel belongs to, when it is
   * one. Every such section renders as Section.CUSTOM_MARKERS, so without it
   * two named sections and the appendix would head their results tables with
   * one and the same "Other Lab Results".
   */
  practitionerSectionName?: string;
  /**
   * The key this section's Notes tab writes under, when it is not the section
   * value itself (practitionerSectionStateKey). Same reason: notes are keyed by
   * section, and every practitioner-named section IS Section.CUSTOM_MARKERS, so
   * without this a note typed under "IgG Allergens" would also appear under
   * "Mycotoxins" and in the Other Lab Results appendix.
   */
  noteSection?: string;
};

type State = {
  bloodworkNote: BloodworkNote;
};

export class TabsComponent extends React.Component<Props, {}> {
  state: State = {
    bloodworkNote: this.initBloodworkNote(
      this.props.noteSection || this.props.section,
      this.props.bloodworkAnalysis,
    ),
  };

  componentWillReceiveProps(nextProps) {
    const { section, noteSection, bloodworkAnalysis } = nextProps;
    this.setState({
      bloodworkNote: this.initBloodworkNote(
        noteSection || section,
        bloodworkAnalysis,
      ),
    });
  }

  /** The key this section's note is stored under. */
  private noteKey(): string {
    return this.props.noteSection || this.props.section;
  }

  initBloodworkNote(
    section: string,
    bloodworkAnalysis: BloodworkAnalysis,
  ): BloodworkNote {
    if (
      bloodworkAnalysis &&
      bloodworkAnalysis.bloodwork &&
      bloodworkAnalysis.bloodwork.bloodworkNotes
    ) {
      let bloodworkNote =
        bloodworkAnalysis.bloodwork.bloodworkNotes.findNote(section);
      if (!bloodworkNote) {
        bloodworkNote = bloodworkAnalysis.bloodwork.bloodworkNotes.addNote(
          section,
          '',
        );
      }
      return new BloodworkNote(bloodworkNote);
    }
    return new BloodworkNote();
  }

  handleInputChange = (event: any) => {
    const value = event.target.value;
    this.setState({
      bloodworkNote: new BloodworkNote({ section: this.noteKey(), text: value }),
    });
  };

  saveOrUpdate = (event: any) => {
    const { bloodworkAnalysis, bloodworkEntryStore } = this.props;
    event.preventDefault();
    const bloodworkNote =
      bloodworkAnalysis.bloodwork.bloodworkNotes.findNote(this.noteKey());
    bloodworkNote.text = this.state.bloodworkNote.text;
    bloodworkEntryStore.saveBloodwork(bloodworkAnalysis.bloodwork).catch((err) => {
      console.error('Failed to save bloodwork note', err);
      alert('Failed to save your note. Please try again.');
    });
  };

  showInsightsTab(): boolean {
    const { section, bloodworkAnalysis, furtherConsiderations } = this.props;
    if (section === Section.PLATELETS) {
      // Blood Clotting & Flow shows a Viscosity insight (the calculation)
      // only when viscosity could be calculated (HCT + total protein present).
      return ViscosityInsights.shouldRender(bloodworkAnalysis);
    }
    /*
     * STRESS_ADRENAL joined 2026-09-02. This allowlist is the same RBC/WBC/
     * GLUCOSE trio that pdf-section.tsx carried, and it was never widened as
     * sections gained Interpretation content: adrenal has 49 pattern rows
     * (Logic + Pattern Description) and 280 gauge rows authored, live in slava
     * and reachable — possibleRootCauseCategoriesFor has a STRESS_ADRENAL case
     * and adrenalRootCauseById pushes the firing pattern ids — with no tab to
     * read them in. The Supplements allowlist below had already picked the
     * section up in 2026-08; this one was simply missed.
     *
     * Membership alone does not open the tab. The section must also have
     * Interpretation rows on a category that actually fired, mirroring
     * showSupplementsTab / showReferencesTab, so a test with no adrenal
     * findings never shows an empty pane.
     */
    if (section === Section.STRESS_ADRENAL) {
      return bloodworkAnalysis
        .possibleRootCauseCategoriesFor(section)
        .some(
          category =>
            furtherConsiderations.findConsiderations(
              category.rootCauseCategory,
              section,
              'Interpretation',
            ).length,
        );
    }
    return [Section.RBC, Section.WBC, Section.GLUCOSE].includes(section);
  }

  showPossibleRootCausesTab(): boolean {
    const { section } = this.props;
    return section !== Section.RBC && !this.isCustomMarkersSection();
  }

  /**
   * The practitioner's own per-test markers. They have no FurtherConsiderations,
   * no About descriptions and no root-cause categories behind them, so the
   * section shows the Reference Range tab and nothing else.
   */
  isCustomMarkersSection(): boolean {
    return this.props.section === Section.CUSTOM_MARKERS;
  }

  showOtherTabs(): boolean {
    const { section } = this.props;
    return section === Section.RBC;
  }

  showFurtherConsiderationsTab(): boolean {
    const { section } = this.props;
    return [Section.RBC, Section.WBC, Section.GLUCOSE].includes(section);
  }

  showSupplementsTab(recommendations: any[]): boolean {
    const { section, furtherConsiderations } = this.props;
    const categories =
      this.props.bloodworkAnalysis.possibleRootCauseCategoriesFor(section);

    return (
      // Everywhere but prod — see supplementsVisible() for the fail-closed
      // env check shared by all supplement features.
      supplementsVisible() &&
      // An allowlist rather than "any section with Supplements rows": a
      // section only belongs here once its rows are authored and reviewed, and
      // an accidental row elsewhere should not open a tab on its own.
      //
      // NUTRIENTS joined 2026-08 — its "< optimal" categories (8010 Calcium,
      // 8230 Vitamin B12, and so on) gained Supplements rows at
      // subCategoryId 9. Digestion has 15 authored rows (cat 85000+) and is
      // still absent: Section.DIGESTION exists now, but nothing reaches those
      // rows, because possibleRootCauseCategoriesFor has no DIGESTION case and
      // its default branch hands back the global root-cause list, whose ids
      // never match an 85000+ row.
      //
      // WHEN THAT DETECTION LANDS, this allowlist is not the only gate to
      // check: the Holobiome by-system cards walk every section in
      // SECTION_HEADINGS (Digestion included), and everything they list starts
      // TICKED on the client's Recommended Supplements page. Digestion rows
      // would reach a client document before this tab ever showed them to the
      // practitioner.
      //
      // INFLAMMATION_OXIDATIVE_STRESS joined 2026-08 with one marker authored:
      // 13040 (▲ hs-CRP) at subCategoryId 3. Detection was already wired —
      // possibleRootCauseCategoriesFor has a case for the section and
      // InflammationOxidativeStressAnalysis pushes 13040 — so this listing and
      // the DB rows had to ship together for exactly the reason above: the
      // section is in SECTION_HEADINGS, so its rows reach the client's
      // Recommended Supplements card whether or not this tab is open.
      //
      // STRESS_ADRENAL joined 2026-08 for the same reason, and it was already
      // overdue: its 19 pattern Supplements rows (cat 90xxx) are reachable —
      // possibleRootCauseCategoriesFor has a STRESS_ADRENAL case and the
      // section is in SECTION_HEADINGS — so those products were already
      // reaching the roadmap and the client's ticked-by-default list with no
      // practitioner-facing tab to review them in.
      (section === Section.RBC ||
        section === Section.WBC ||
        section === Section.GLUCOSE ||
        section === Section.NUTRIENTS ||
        section === Section.INFLAMMATION_OXIDATIVE_STRESS ||
        section === Section.STRESS_ADRENAL) &&
      categories.some(
        category =>
          furtherConsiderations.findConsiderations(
            category.rootCauseCategory,
            section,
            'Supplements',
            this.props.enabledSupplementBrands,
          ).length,
      )
    );
  }

  showReferencesTab(): boolean {
    const { section, furtherConsiderations } = this.props;
    const categories =
      this.props.bloodworkAnalysis.possibleRootCauseCategoriesFor(section);

    return (
      // The section list lives in reference-tab-sections.ts, shared with the
      // Notes digest so the two cannot drift apart. Membership alone does not
      // open the tab: the section must also have References rows on a category
      // that actually fired.
      sectionHasReferencesTab(section) &&
      categories.some(
        category =>
          furtherConsiderations.findConsiderations(
            category.rootCauseCategory,
            section,
            'References',
          ).length,
      )
    );
  }

  refRangeTitle(): string {
    const { section } = this.props;
    if (section === Section.WBC) {
      return 'Immune Activity Results';
    } else if (section === Section.PLATELETS) {
      return 'Blood Clotting & Flow Results';
    }
    else if (section === Section.PROTEINS) {
      return 'Protein Results';
    }
    else if (section === Section.NUTRIENTS) {
      return 'Vitamin & Mineral Results';
    }
    else if (section === Section.SEX_HORMONE) {
      return 'Hormone Results';
    }
    else if (section === Section.INFLAMMATION_OXIDATIVE_STRESS) {
      return 'Inflammation & Oxidative Stress Results';
    }
    else if (section === Section.LIPIDS) {
      return 'Lipid Results';
    }
    else if (section === Section.LIVER_BILIARY) {
      return 'Liver Results';
    }
    else if (section === Section.ELECTROLYTES) {
      return 'Electrolyte Results';
    }
    else if (section === Section.STRESS_ADRENAL) {
      return 'Stress & Adrenal Results';
    }
    else if (section === Section.GLUCOSE) {
      return 'Glucose Regulation & Metabolic Function Results';
    }
    else if (section === Section.CUSTOM_MARKERS) {
      // A section the practitioner named is titled by that name, so a report
      // with two of them plus the appendix does not print one heading three
      // times over.
      if (this.props.practitionerSectionName) {
        return `${this.props.practitionerSectionName} Results`;
      }
      // The default would read "Other Lab Results Results".
      return Section.CUSTOM_MARKERS;
    }
    return `${section} Results`;
  }

  render() {
    const {
      section,
      bloodworkAnalysis,
      furtherConsiderations,
      panelMarkers,
      bloodworkEntryStore,
      infoLink,
      enabledSupplementBrands,
    } = this.props;

    const supplementRecommendations = SupplementsEngine.getRecommendations(
      section,
      bloodworkAnalysis,
      enabledSupplementBrands || [],
    );

    return (
      // mountOnEnter: react-bootstrap renders EVERY <Tab> pane up front by
      // default (its own default is `mountOnEnter: false`), so opening a report
      // built the Possible Root Causes, About, References, Supplements and
      // Notes trees for all 18 sections before the practitioner had looked at
      // anything but Reference Range — each one scanning the whole
      // FurtherConsiderations table as it went. Panes now build on first visit.
      //
      // NOT unmountOnExit: once a tab has been opened it stays mounted, so
      // switching away from Notes and back cannot discard unsaved text.
      <Tabs defaultActiveKey={1} id="uncontrolled-tab-example" mountOnEnter>
        <Tab eventKey={1} title="Reference Range">
          <br />
          <ReferenceRangeAnalysisContainer
            title={this.refRangeTitle()}
            section={section}
            bloodwork={bloodworkAnalysis.bloodwork}
            previousBloodwork={bloodworkAnalysis.previousBloodwork}
            infoLink={infoLink}
            panelMarkers={panelMarkers}
            textMarkers={sectionTextMarkers(
              bloodworkAnalysis,
              section,
              this.props.practitionerSectionName,
            )}
          />
        </Tab>

        {this.showInsightsTab() ? (
          <Tab eventKey={2} title="Insights">
            <br />
            {section === Section.PLATELETS ? (
              <ViscosityInsights bloodworkAnalysis={bloodworkAnalysis} />
            ) : (
              <FurtherConsiderationsSection
                section={section}
                tab="Interpretation"
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
              />
            )}
          </Tab>
        ) : null}

        {this.showPossibleRootCausesTab() ? (
          <Tab eventKey={3} title="Possible Root Causes">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Causes"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={4} title="Additional Testing">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Testing"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showFurtherConsiderationsTab() ? (
          <Tab eventKey={5} title="Further Considerations">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Considerations"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={6} title="Signs and Symptoms">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Symptoms"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={7} title="Diet">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Diet"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showOtherTabs() ? (
          <Tab eventKey={8} title="Lifestyle">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Lifestyle"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showSupplementsTab(supplementRecommendations) ? (
          <Tab eventKey={9} title="Supplements">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="Supplements"
              brands={enabledSupplementBrands}
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {!this.isCustomMarkersSection() ? (
          <Tab eventKey={12} title="About">
            <br />
            <AboutSection
              section={section}
              panelMarkers={panelMarkers}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {this.showReferencesTab() ? (
          <Tab eventKey={11} title="References">
            <br />
            <FurtherConsiderationsSection
              section={section}
              tab="References"
              bloodworkAnalysis={bloodworkAnalysis}
              furtherConsiderations={furtherConsiderations}
            />
          </Tab>
        ) : null}

        {/* Notes are offered here too: the practitioner chose to add these
            markers, so they are the ones most likely to need a sentence of
            explanation. Only the tabs with no CONTENT behind them (Insights,
            root causes, About...) stay hidden for this section. The note is
            keyed by the Section VALUE, like every other section's — see the
            rename remap in UserProfile if that value ever changes again. */}
        <Tab eventKey={10} title="Notes">
          <br />
          <BloodworkNotes
            section={this.noteKey() as Section}
            title="Add your own notes related to this analysis here. Click Save when finished."
            bloodwork={bloodworkAnalysis.bloodwork}
            bloodworkEntryStore={bloodworkEntryStore}
            // These props power the "gather from tabs" digest under Save.
            bloodworkAnalysis={bloodworkAnalysis}
            furtherConsiderations={furtherConsiderations}
            panelMarkers={panelMarkers}
          />
        </Tab>
      </Tabs>
    );
  }
}
